// iter63 — Top-bar Team Directory popover.
// Shows pageable users with online status + quick Page / Note buttons.
// Sits in the Layout top bar next to the Personal Audio button.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Users, Search, Megaphone, MessageSquarePlus, Phone, BellOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

function api() {
  const token = localStorage.getItem('token');
  return axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {} });
}

const ROLE_LABEL = {
  Admin: 'Admin', InventoryManager: 'Inventory Mgr',
  ProductionSupervisor: 'Supervisor', Purchasing: 'Purchasing', Operator: 'Operator',
};
const ROLE_TONE = {
  Admin: 'bg-purple-50 border-purple-300 text-purple-700',
  ProductionSupervisor: 'bg-blue-50 border-blue-300 text-blue-700',
  InventoryManager: 'bg-emerald-50 border-emerald-300 text-emerald-700',
  Purchasing: 'bg-amber-50 border-amber-300 text-amber-700',
  Operator: 'bg-slate-50 border-slate-300 text-slate-700',
};

const initials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || '?';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export default function TeamDirectory() {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('online');
  const [pageOpen, setPageOpen] = useState(null);    // user object for quick page
  const [noteOpen, setNoteOpen] = useState(null);    // user object for quick note
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api().get('/audio/pageable-users');
      setUsers(r.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => {
    if (!open) return;
    const t = setInterval(load, 30000); // refresh online status
    return () => clearInterval(t);
  }, [open, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = users;
    if (q) rows = rows.filter(u => (u.name || '').toLowerCase().includes(q) || (u.role || '').toLowerCase().includes(q));
    if (tab === 'online') rows = rows.filter(u => u.online);
    return rows;
  }, [users, search, tab]);

  const onlineCount = users.filter(u => u.online).length;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="relative" title="Team Directory" data-testid="team-directory-trigger">
            <Users className="h-5 w-5 text-slate-600" />
            {onlineCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-500" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[380px] p-0" align="end" data-testid="team-directory-popover">
          <div className="border-b px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-slate-600" />
                <div className="text-sm font-semibold">Team</div>
                <Badge variant="outline" className="bg-emerald-50 border-emerald-300 text-emerald-700 text-[10px]">
                  {onlineCount} online
                </Badge>
              </div>
              <Button size="sm" variant="ghost" onClick={() => { setOpen(false); navigate('/inbox'); }} data-testid="team-directory-view-all" className="text-xs">
                Inbox
              </Button>
            </div>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input className="pl-7 h-8 text-sm" placeholder="Search teammates" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="team-directory-search" />
            </div>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 mx-3 my-2" data-testid="team-directory-tabs">
              <TabsTrigger value="online" data-testid="team-tab-online">Online ({onlineCount})</TabsTrigger>
              <TabsTrigger value="all" data-testid="team-tab-all">All ({users.length})</TabsTrigger>
            </TabsList>
            <TabsContent value={tab} className="px-2 pb-3 max-h-96 overflow-y-auto">
              {filtered.length === 0 && (
                <div className="text-xs text-slate-500 py-6 text-center">
                  {tab === 'online' ? 'No teammates currently online.' : 'No matches.'}
                </div>
              )}
              <div className="space-y-1">
                {filtered.map(u => (
                  <div key={u.id} className={`flex items-center gap-2 p-2 rounded hover:bg-slate-50 ${u.dnd ? 'opacity-70' : ''}`} data-testid={`team-row-${u.id}`}>
                    <div className="relative">
                      <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                        {initials(u.name)}
                      </div>
                      {u.online && <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 border-2 border-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{u.name}</div>
                      <div className="flex items-center gap-1">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${ROLE_TONE[u.role] || 'bg-slate-50 border-slate-200'}`}>{ROLE_LABEL[u.role] || u.role}</span>
                        {u.dnd && <span className="text-[10px] text-red-600 flex items-center gap-0.5"><BellOff className="h-2.5 w-2.5" />DND</span>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        title="Page"
                        disabled={!u.can_page || u.dnd}
                        onClick={() => { setPageOpen(u); setOpen(false); }}
                        data-testid={`team-page-${u.id}`}
                      >
                        <Megaphone className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        title="Send note"
                        disabled={!u.can_page}
                        onClick={() => { setNoteOpen(u); setOpen(false); }}
                        data-testid={`team-note-${u.id}`}
                      >
                        <MessageSquarePlus className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </PopoverContent>
      </Popover>

      {pageOpen && <QuickPageDialog user={pageOpen} onClose={() => setPageOpen(null)} />}
      {noteOpen && <QuickNoteDialog user={noteOpen} onClose={() => setNoteOpen(null)} />}
    </>
  );
}

function QuickPageDialog({ user, onClose }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!text.trim()) { toast.error('Type a message'); return; }
    setSending(true);
    try {
      await api().post('/audio/page', { recipients: [user.id], mode: 'tts', text: text.trim(), priority: 'normal' });
      toast.success(`Paged ${user.name}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Page failed');
    } finally { setSending(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="quick-page-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Megaphone className="h-4 w-4" /> Page {user.name}</DialogTitle>
          <DialogDescription>They'll hear a chime + this message via text-to-speech.</DialogDescription>
        </DialogHeader>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={400} placeholder={`Hi ${user.name?.split(' ')[0] || ''}, please come to…`} data-testid="quick-page-text" />
        <div className="text-[10px] text-slate-500 text-right">{text.length}/400</div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={sending || !text.trim()} onClick={send} data-testid="quick-page-send">
            <Phone className="h-4 w-4 mr-2" />{sending ? 'Sending…' : 'Send Page'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuickNoteDialog({ user, onClose }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!body.trim()) { toast.error('Type a message'); return; }
    setSending(true);
    try {
      await api().post('/messages', { recipients: [user.id], subject: subject.trim(), body: body.trim() });
      toast.success(`Note sent to ${user.name}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="quick-note-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageSquarePlus className="h-4 w-4" /> Note to {user.name}</DialogTitle>
          <DialogDescription>An async message — they'll see it in their Inbox.</DialogDescription>
        </DialogHeader>
        <Input placeholder="Subject (optional)" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} data-testid="quick-note-subject" />
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} maxLength={5000} placeholder="Type your note…" data-testid="quick-note-body" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={sending || !body.trim()} onClick={send} data-testid="quick-note-send">
            Send Note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
